import PropTypes from "prop-types";
import CloseIcon from "@mui/icons-material/Close";
import { GenericFormField } from "common/Components/GenericForm";
import StatusPill from "common/Components/StatusPill";
import {
  buildCustomerSelectOptionsForRow,
  getPlatformAccountStatusTone,
  isCustomerAccessRowDeactivated,
  PLATFORM_USER_ACCESS_UI,
} from "../PlatformUserAccess.constants";
import {
  AddCustomerButton,
  CustomerAccessBorderedPanel,
  CustomerAccessColumnHeaderLabel,
  CustomerAccessColumnHeaderRow,
  CustomerAccessDescription,
  CustomerAccessError,
  CustomerAccessDeactivatedWarning,
  CustomerAccessEditorContainer,
  CustomerAccessEditorHeader,
  CustomerAccessHeaderText,
  CustomerAccessHeading,
  CustomerAccessCustomerFieldCell,
  CustomerAccessCustomerHeaderCell,
  CustomerAccessRolesFieldCell,
  CustomerAccessRolesHeaderCell,
  CustomerAccessRemoveCell,
  CustomerAccessRemoveHeaderCell,
  CustomerAccessRemoveIconButton,
  CustomerAccessRow,
  CustomerAccessRowInner,
  CustomerAccessRows,
  CustomerAccessAccessStatusCell,
  CustomerAccessStatusHeaderCell,
} from "./CustomerAccessEditor.styles";
import {
  AccessActionButton,
  TableRowActionCell,
} from "./UserAccessList.styles";
import { getCustomerRowAction } from "../../Admin/Users/Users.constants";

const getCustomerField = (customerOptions) => ({
  name: "customerId",
  label: PLATFORM_USER_ACCESS_UI.tableColumns.customer,
  placeholder: PLATFORM_USER_ACCESS_UI.customerAccessCustomerSelectPlaceholder,
  type: "singleSelect",
  options: customerOptions,
  compact: true,
  inline: true,
  disableClearable: true,
});

const getRolesField = (roleOptions, disabled = false) => ({
  name: "roles",
  label: PLATFORM_USER_ACCESS_UI.tableColumns.roles,
  type: "multiSelect",
  options: roleOptions,
  compact: true,
  inline: true,
  valueDisplay: "comma",
  emptyLabel: "No roles",
  menuTitle: PLATFORM_USER_ACCESS_UI.tableColumns.roles,
  disabled,
});

const hasPersistedAccessId = (row) => {
  const accessId = row?.accessId;
  return accessId != null && String(accessId).trim() !== "";
};

const CustomerAccessEditor = ({
  rows,
  customerOptions,
  activeCustomers = [],
  roleOptions,
  userId,
  onAddRow,
  onRemoveRow,
  onRowChange,
  title = PLATFORM_USER_ACCESS_UI.customerAccessTitle,
  showDescription = false,
  showColumnHeaders = true,
  disabled = false,
  hideRemoveForPersistedRows = false,
  rowErrors = [],
  error,
  onAccessActionSuccess,
  onCustomerUserListAction,
}) => {
  const handleFieldChange = (rowIndex) => (name, value) => {
    onRowChange(rowIndex, name, value);
  };
  const onRowAction = async (row, action) => {
    if (!onCustomerUserListAction) {
      return;
    }
    const result = await onCustomerUserListAction(
      row.customerId,
      row.customerCode,
      { ...row, userId },
      action,
      row.roles,
      true
    );
    if (result?.ok) {
      await onAccessActionSuccess?.();
    }
  };
  const handleRowAction = (row, isDeactivated) => {
    if (isDeactivated) {
      return null;
    }
    const rowAction = getCustomerRowAction(row.accessStatus);
    if (!rowAction || !onCustomerUserListAction) {
      return null;
    }
    return (
      <TableRowActionCell onClick={(event) => event.stopPropagation()}>
        <AccessActionButton
          type="button"
          $tone={rowAction.tone}
          onClick={() => onRowAction(row, rowAction.action)}
        >
          {rowAction.label}
        </AccessActionButton>
      </TableRowActionCell>
    );
  };

  return (
    <CustomerAccessEditorContainer>
      <CustomerAccessEditorHeader>
        <CustomerAccessHeaderText>
          <CustomerAccessHeading component="h3">{title}</CustomerAccessHeading>
          {showDescription ? (
            <CustomerAccessDescription component="p">
              {PLATFORM_USER_ACCESS_UI.customerAccessDescription}
            </CustomerAccessDescription>
          ) : null}
          {error ? (
            <CustomerAccessError component="p">{error}</CustomerAccessError>
          ) : null}
        </CustomerAccessHeaderText>
        <AddCustomerButton
          type="button"
          onClick={onAddRow}
          disabled={disabled}
        >
          {PLATFORM_USER_ACCESS_UI.addCustomerLabel}
        </AddCustomerButton>
      </CustomerAccessEditorHeader>
      <CustomerAccessBorderedPanel>
        {showColumnHeaders ? (
          <CustomerAccessColumnHeaderRow>
            <CustomerAccessCustomerHeaderCell>
              <CustomerAccessColumnHeaderLabel variant="caption">
                {PLATFORM_USER_ACCESS_UI.tableColumns.customer}
              </CustomerAccessColumnHeaderLabel>
            </CustomerAccessCustomerHeaderCell>
            <CustomerAccessRolesHeaderCell>
              <CustomerAccessColumnHeaderLabel variant="caption">
                {PLATFORM_USER_ACCESS_UI.tableColumns.roles}
              </CustomerAccessColumnHeaderLabel>
            </CustomerAccessRolesHeaderCell>
            <CustomerAccessStatusHeaderCell>
              <CustomerAccessColumnHeaderLabel variant="caption">
                {PLATFORM_USER_ACCESS_UI.tableColumns.customerAccess}
              </CustomerAccessColumnHeaderLabel>
            </CustomerAccessStatusHeaderCell>
            <CustomerAccessRemoveHeaderCell>
              <CustomerAccessColumnHeaderLabel variant="caption">
                {PLATFORM_USER_ACCESS_UI.actionColumnLabel}
              </CustomerAccessColumnHeaderLabel>
            </CustomerAccessRemoveHeaderCell>
          </CustomerAccessColumnHeaderRow>
        ) : null}
        <CustomerAccessRows>
          {rows.map((row, index) => {
            const errors = rowErrors[index] ?? {};
            const isDeactivated = isCustomerAccessRowDeactivated(
              row,
              activeCustomers
            );
            const showRemoveButton =
              !disabled &&
              !isDeactivated &&
              !(hideRemoveForPersistedRows && hasPersistedAccessId(row));
            return (
              <CustomerAccessRow key={`customer-access-${index}`}>
                <CustomerAccessRowInner>
                  <CustomerAccessCustomerFieldCell>
                    <GenericFormField
                      field={{
                        ...getCustomerField(
                          buildCustomerSelectOptionsForRow(
                            customerOptions,
                            rows,
                            index
                          )
                        ),
                        disabled: disabled || hasPersistedAccessId(row),
                      }}
                      value={row.customerId}
                      allValues={row}
                      onChange={handleFieldChange(index)}
                      error={errors.customerId}
                    />
                  </CustomerAccessCustomerFieldCell>
                  <CustomerAccessRolesFieldCell>
                    <GenericFormField
                      field={getRolesField(
                        roleOptions,
                        disabled || isDeactivated || !row.customerId
                      )}
                      value={row.roles}
                      allValues={row}
                      onChange={handleFieldChange(index)}
                      error={errors.roles}
                    />
                  </CustomerAccessRolesFieldCell>
                  <CustomerAccessAccessStatusCell>
                    {row.accessStatus ? (
                      <StatusPill
                        label={row.accessStatus}
                        tone={getPlatformAccountStatusTone(row.accessStatus)}
                      />
                    ) : null}
                  </CustomerAccessAccessStatusCell>
                  <CustomerAccessRemoveCell>
                    {showRemoveButton ? (
                      <CustomerAccessRemoveIconButton
                        size="small"
                        aria-label={
                          PLATFORM_USER_ACCESS_UI.removeCustomerRowAriaLabel
                        }
                        onClick={() => onRemoveRow(index)}
                      >
                        <CloseIcon fontSize="small" />
                      </CustomerAccessRemoveIconButton>
                    ) : (
                      handleRowAction(row, isDeactivated)
                    )}
                  </CustomerAccessRemoveCell>
                </CustomerAccessRowInner>
                {isDeactivated ? (
                  <CustomerAccessDeactivatedWarning>
                    {PLATFORM_USER_ACCESS_UI.customerDeactivatedWarning}
                  </CustomerAccessDeactivatedWarning>
                ) : null}
              </CustomerAccessRow>
            );
          })}
        </CustomerAccessRows>
      </CustomerAccessBorderedPanel>
    </CustomerAccessEditorContainer>
  );
};

CustomerAccessEditor.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  customerOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  activeCustomers: PropTypes.arrayOf(PropTypes.object),
  roleOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  onAddRow: PropTypes.func.isRequired,
  onRemoveRow: PropTypes.func.isRequired,
  onRowChange: PropTypes.func.isRequired,
  title: PropTypes.string,
  showDescription: PropTypes.bool,
  showColumnHeaders: PropTypes.bool,
  disabled: PropTypes.bool,
  hideRemoveForPersistedRows: PropTypes.bool,
  rowErrors: PropTypes.arrayOf(PropTypes.object),
  error: PropTypes.node,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onAccessActionSuccess: PropTypes.func,
  onCustomerUserListAction: PropTypes.func,
};

CustomerAccessEditor.defaultProps = {
  disabled: false,
  activeCustomers: [],
};

export default CustomerAccessEditor;
